import { Box, Grid, Typography } from '@mui/material'
import StatCard from './StatCard'
import StatChart from './StatChart'
import { fmtDate, getWorkoutTitle, unixTimestampsFromStartTime, mapPunchesToTargets, computeStats } from './util'
import { usePaginatedCollection , useUser } from '@/pb'
import { useMemo, useState } from 'react'



export default function AnalyticsScreen({ workoutTemplates }) {
  const { user } = useUser()
  const [workoutHistoryPage, setWorkoutHistoryPage] = useState(1)
  const {
    records: workoutHistory,
    loaded: workoutHistoryLoaded,
  } = usePaginatedCollection('music_workouts', {
    workoutHistoryPage,
    perPage: 30,
    sort: '-created',
    filter: `userId = "${user.id}"`
  })

  const workoutHistoryExtended = useMemo(() => {
    if (!workoutHistoryLoaded || !workoutTemplates) return [];

    return workoutHistory.map((workout) => {
      const template = workoutTemplates.find((w) => w.id === workout.templateId);

      if (!workout.punches || !template) {
        return {
          ...workout,
          workoutTitle: 'Unknown',
          targets: [],
          punches: [],
          targetsMap: {},
          stats: null,
          averageDelay: null,
          efficiency: null,
        };
      }

      const workoutTitle = getWorkoutTitle(template);
      const targets = template.punchTargets;
      const punchTimestamps = workout.punches.map(punch => punch.timestamp)
      const punchForces = workout.punches.map(punch => punch.force);
      const punchesFull = workout.punches.map(punch => ({
        ...punch,
        timestamp: punch.timestamp - workout.startTime
      }));

      const avgForce = (punchForces.reduce((a, b) => a + b, 0) / punchForces.length).toFixed(0);

      const targetsMap = mapPunchesToTargets(punchTimestamps, targets);
      const stats = computeStats(targetsMap, targets, punchTimestamps);

      return {
        ...workout,
        template,
        workoutTitle,
        targets,
        punchTimestamps,
        punchForces,
        punchesFull,
        targetsMap,
        stats,
        avgForce,
        averageDelay: stats ? stats.avgDelay.toFixed(2) : null,
        efficiency: stats ? stats.efficiency.toFixed(0) : null,
      };
    });
  }, [workoutHistory, workoutTemplates, workoutHistoryLoaded]);

  const averages = useMemo(() => {
    if (!workoutHistoryExtended || workoutHistoryExtended.length === 0) return null
    const valid = workoutHistoryExtended.filter(
      (w) =>
        w.efficiency != null &&
        w.avgForce != null &&
        w.averageDelay != null
    )
    if (valid.length === 0) return null

    const sumEff = valid.reduce((s, w) => s + Number(w.efficiency), 0)
    const sumForce = valid.reduce((s, w) => s + Number(w.avgForce), 0)
    const sumDelay = valid.reduce((s, w) => s + Number(w.averageDelay), 0)

    return {
      avgEfficiency: Math.round(sumEff / valid.length),
      avgForce: Math.round(sumForce / valid.length),
      avgDelay: (sumDelay / valid.length).toFixed(2),
      count: valid.length,
    }
  }, [workoutHistoryExtended])

  const relevantWorkouts = useMemo(() => {
    if (!workoutHistoryExtended || workoutHistoryExtended.length === 0) return []
    const valid = workoutHistoryExtended.filter(
      (w) =>
        w.efficiency != null &&
        w.avgForce != null &&
        w.averageDelay != null
    )
    // asc by date
    const sorted = [...valid].sort((a, b) => {
      const dateA = new Date(a.created || a.updated || 0)
      const dateB = new Date(b.created || b.updated || 0)
      return dateA - dateB
    })
    return sorted.slice(-30) // last 30
  }, [workoutHistoryExtended])

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
        Аналитика
      </Typography>

      {averages ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Средние данные последних {averages.count} тренировок
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={4}>
              <StatCard label="Эффективность" value={`${averages.avgEfficiency}%`} />
            </Grid>
            <Grid item xs={4}>
              <StatCard label="Средняя сила" value={averages.avgForce} />
            </Grid>
            <Grid item xs={4}>
              <StatCard label="Среднее отставание" value={`${averages.avgDelay}s`} />
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 2 }}>
            Графики статистики последних {relevantWorkouts.length} тренировок
          </Typography>

          <StatChart workouts={relevantWorkouts} dataKey="efficiency" label="Эффективность" unit="%" />
          <StatChart workouts={relevantWorkouts} dataKey="avgForce" label="Средняя сила" />
          <StatChart workouts={relevantWorkouts} dataKey="averageDelay" label="Среднее отставание" unit="s" />
        </>
      ) : (
        <Typography color="text.secondary">Нет данных</Typography>
      )}
    </Box>
  )
}